"use client";
import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation"; // Add this import
import { ContentContainer, Layout, Stack } from "./molecules";
import thumbAvtar from "../../public/thumbAvtar.svg";
import { getTestimonialsBySlug } from "../actions/courseNew";
import { getPTestimonialsBySlug } from "../actions/pCourse/index.js";
import { useDictionary } from "../context/dictionaryProvider";
import Image from "next/image";
import Linkdin from "../../public/linkdin-icon.svg";
import Google from "../../public/google-icon.svg";

// Dynamically import the ReviewCarousel component
const ReviewCarousel = dynamic(() => import("./ReviewCarousel"));

// Skeleton loader component
const ClientReviewSkeleton = () => (
  <div className="w-full animate-pulse flex flex-col gap-7 items-center text-center">
    <div className="w-24 h-24 bg-gray-300 rounded-full"></div>
    <div className="w-3/4 h-10 bg-gray-300 rounded-md"></div>
    <div className="w-full h-64 bg-gray-300 rounded-md"></div>
  </div>
);

const ClientReview = ({ slug, params, sourceType = "course" }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const dict = useDictionary();
  const pathname = usePathname(); // Get current path

  // Check if current page is homepage
  const isHomePage = pathname === "/" || pathname === "/en" || pathname === "/ar";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await (
          sourceType === "p_course"
            ? getPTestimonialsBySlug
            : getTestimonialsBySlug
        )({
          ...params,
          slug,
          limit: 25,
        });
        setTestimonials(response?.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, params, sourceType]);

  // Filter reviews based on selected tab (only if not homepage)
  const filteredReviews = isHomePage
    ? testimonials
    : activeTab === "all"
    ? testimonials
    : testimonials.filter((review) => {
        const platform = review.platform?.toLowerCase().trim();
        if (platform?.includes("link")) return activeTab === "linkedin";
        if (platform?.includes("google")) return activeTab === "google";
        return platform === activeTab;
      });

  if (loading) {
    return (
      <Layout className="clientReview mt-16 lg:mt-32 mb-8">
        <ContentContainer>
          <Stack>
            <ClientReviewSkeleton />
          </Stack>
        </ContentContainer>
      </Layout>
    );
  }

  return (
    <>
      {testimonials?.length > 0 && (
        <Layout className="clientReview mt-16 lg:mt-32 mb-8" data-track-view="reviews_view">
          <ContentContainer>
            <Stack>
              {/* Heading */}
              <div className="relative flex flex-col w-full items-center justify-center text-center gap-7">
                <Image
                  src={thumbAvtar.src}
                  alt="thumb"
                  height={110}
                  width={110}
                  loading="lazy"
                />
                <p className="text-[32px] lg:text-5xl font-extrabold leading-normal">
                  {dict?.clientReview?.title || "Client Reviews"}
                </p>
              </div>

              {/* Tabs - Hide only this section on homepage */}
              {!isHomePage && (
                <div className="flex items-center justify-center gap-5 mt-6 border border-green-300 rounded-full p-2 max-w-xl mx-auto">
                  {/* All Reviews */}
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                      activeTab === "all"
                        ? "bg-green-100 text-green-600"
                        : "text-gray-600 hover:text-green-600"
                    }`}
                  >
                    All Reviews
                  </button>

                  {/* LinkedIn */}
                  <button
                    onClick={() => setActiveTab("linkedin")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
                      activeTab === "linkedin"
                        ? "bg-green-100 text-green-600"
                        : "text-gray-600 hover:text-green-600"
                    }`}
                  >
                    <Image src={Linkdin} alt="LinkedIn" width={20} height={20} />
                    LinkedIn
                  </button>

                  {/* Google */}
                  <button
                    onClick={() => setActiveTab("google")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
                      activeTab === "google"
                        ? "bg-green-100 text-green-600"
                        : "text-gray-600 hover:text-green-600"
                    }`}
                  >
                    <Image src={Google} alt="Google" width={20} height={20} />
                    Google
                  </button>
                </div>
              )}

              {/* Review Carousel - Always shows */}
              <ReviewCarousel reviews={filteredReviews} />
            </Stack>
          </ContentContainer>
        </Layout>
      )}
    </>
  );
};

export default ClientReview;
